import { useState } from 'react';

import { Card, Icon } from '@iwoca/orion';
import Rive from '@rive-app/react-canvas';
import classnames from 'classnames';

import styles from './CheckEligibility.module.css';
// @ts-expect-error does not recognise riv import.
import successTick from '../../assets/animation/successTick.riv';
import { calculateEligibleAmount } from '../../helpers/calculateEligibleAmount';
import { useNumberFormat } from '../../hooks/useNumberFormat';
import { useSignupRouterUK } from '../../hooks/useSignupRouter/useSignupRouterUK';
import { useSplitTest } from '../../hooks/useSplitTest';
import {
  useGetApplicationQuery,
  useGetSelectStateQuery,
  useGetStateQuery,
} from '../../lendingApi/lendingApiQueries';
import { TGetStateResponse, TSignupStepsUK } from '../../types';
import { Button } from '../Button/Button';
import { normaliseStepData } from '../helpers/stateNormalisation';
import { CHECK_ELIGIBILITY_STEP_ID } from '../helpers/steps';

const CheckEligibility = () => {
  const [navigatingToStep, setIsNavigatingToStep] =
    useState<TSignupStepsUK | null>(null);

  const { formatCurrency } = useNumberFormat();
  const { navigateSignup } = useSignupRouterUK();

  const { isStateFetching } = useGetStateQuery<TGetStateResponse>();
  const { normalisedState } = useGetSelectStateQuery(
    (data) => normaliseStepData(data)[CHECK_ELIGIBILITY_STEP_ID],
  );

  const { hasInterestOnlyEnabled } = useSplitTest();
  const { application } = useGetApplicationQuery();

  const requestedAmount = application.requested_products?.[0].amount;

  const eligibleAmount = calculateEligibleAmount({
    annualTurnover: normalisedState.last12MonthsTurnover || 0,
    requestedAmount,
  });

  const navigateWithLoading = async (route: TSignupStepsUK) => {
    setIsNavigatingToStep(route);

    await navigateSignup({ route });

    setIsNavigatingToStep(null);
  };

  return (
    <section className={styles.checkEligibilityContainer}>
      <div className={styles.animationContainer}>
        <Rive
          src={successTick}
          className={styles.accountSuccessTickRive}
          data-testid="success-tick-animation"
        />
      </div>
      <h1 className={styles.title}>
        {requestedAmount && requestedAmount > eligibleAmount
          ? 'Good news, you’re eligible*'
          : `You’re eligible for up to ${formatCurrency(eligibleAmount)}*`}
      </h1>
      <Card
        className={classnames(styles.benefitsCard, {
          [styles.benefitsWithInterestOnly]: hasInterestOnlyEnabled,
        })}
      >
        <section className={styles.benefitsContainer}>
          <div className={styles.benefit}>
            <Icon icon="starOutline" className={styles.benefitIcon} />
            <p>Repay early for free at any time</p>
          </div>
          <div className={styles.benefit}>
            <Icon icon="calendarOutline" className={styles.benefitIcon} />
            <p>Borrow from 1 day to 60 months</p>
          </div>
          <div className={styles.benefit}>
            <Icon icon="clock2" className={styles.benefitIcon} />
            <p>Decision within 24 hours</p>
          </div>
          {hasInterestOnlyEnabled && (
            <div className={styles.benefit}>
              <Icon icon="exchange" className={styles.benefitIcon} />
              <p>3-month interest-only repayment option</p>
            </div>
          )}
        </section>
        <p className={styles.terms}>
          <small>
            *Final terms are subject to the strength of your application
          </small>
        </p>
        <p className={styles.ctaLabel}>
          <strong>Applying won’t affect your credit score</strong>
        </p>
        <Button
          className={styles.nextButton}
          disabled={navigatingToStep === 'business' || isStateFetching}
          loading={navigatingToStep === 'applicant'}
          type="submit"
          onClick={() => navigateWithLoading('applicant')}
        >
          Next, add personal details
        </Button>
      </Card>
      <Button
        variant="tertiary"
        className={styles.backButton}
        disabled={navigatingToStep === 'applicant' || isStateFetching}
        loading={navigatingToStep === 'business'}
        onClick={() => navigateWithLoading('business')}
        type="button"
      >
        Back
      </Button>
    </section>
  );
};

export default CheckEligibility;
